import React, { useEffect, useState } from "react";
import "./PrivacyPolicy.css";
import DynamicBanner from "../../Pages/include/DynamicBanner";
import api from "../../../api"; // adjust path if needed

// Helper to ensure headings have numbers (1., 2., 3.)
const ensurePrefixed = (raw, prefixNumber, defaultText) => {
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return `${prefixNumber}. ${defaultText}`;
  }
  // If already starts with "1." or "1. " etc, keep as-is
  const regex = new RegExp(`^\\s*${prefixNumber}\\s*\\.`, "i");
  return regex.test(raw) ? raw : `${prefixNumber}. ${raw}`;
};

const PrivacyPolicy = ({ pageData: initialPageData = null }) => {
  const [pageData, setPageData] = useState(initialPageData);
  const [loading, setLoading] = useState(!initialPageData);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialPageData) return;
    let mounted = true;
    const fetchPage = async () => {
      try {
        setLoading(true);
        const res = await api.get("/api/sitepages/academic/campus-life");
        const items = res.data?.data || res.data || [];
        const privacy = items.find((it) => (it.page_slug || "").toLowerCase() === "privacy");
        
        if (!privacy) throw new Error("Privacy page not found in campus-life data");
        
        if (mounted) {
          setPageData(privacy);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading privacy page:", err);
        if (mounted) {
          setError(err.message || "Failed to load privacy page");
          setLoading(false);
        }
      }
    };
    fetchPage();
    return () => { mounted = false; };
  }, [initialPageData]);

  if (loading) return null; 
  if (error) return <div className="container py-4">Error loading page: {error}</div>;
  if (!pageData) return null;

  const banner = pageData.banner || {};
  const content = pageData.content || {};

  // --- Intro Section ---
  const introHeading = content.intro_heading || "";
  const introTextHtml = content.intro_text || "";

  // --- 1. Interpretation ---
  const interpretationHeadingRaw = content.interpretation_heading || "Interpretation";
  const interpretationHeading = ensurePrefixed(interpretationHeadingRaw, 1, "Interpretation");

  // --- 2. Definitions ---
  const definitionsHeadingRaw = content.definitions_heading || "Definitions";
  const definitionsHeading = ensurePrefixed(definitionsHeadingRaw, 2, "Definitions");
  const definitions = Array.isArray(content.definitions) ? content.definitions : (content.definitions || []);

  // --- 3. Data Collection ---
  const dataCollectionHeadingRaw = content.data_collection_heading || "Collecting and Using Your Personal Data";
  const dataCollectionHeading = ensurePrefixed(dataCollectionHeadingRaw, 3, "Collecting and Using Your Personal Data");
  const dataCollectionSections = content.data_collection_sections || [];

  // --- Contact Section ---
  const contactHeadingRaw = content.contact_heading || "Contact Us";
  const contactHeading = contactHeadingRaw;
  const contactText = content.contact_text || "";
  const contactUrl = content.contact_url || "";
  const contactIcon = content.contact_icon || "fa-paper-plane-o";

  // --- Helper logic for Definitions layout ---
  const numberedDefs = definitions.map((defHtml, idx) => ({
    number: `2.${idx + 1}`,
    html: defHtml,
  }));
  const mid = Math.ceil(numberedDefs.length / 2);
  const leftDefs = numberedDefs.slice(0, mid);
  const rightDefs = numberedDefs.slice(mid);

  return (
    <>
      {/* This style block forces the bullets (disc) and indentation 
        to appear for content inside '.summernote-content'.
      */}
      <style>
        {`
          .summernote-content ul {
     
            margin-bottom: 1rem;
          }
          .summernote-content ol {
            list-style-type: decimal !important;
            padding-left: 2rem !important;
            margin-bottom: 1rem;
          }
          .summernote-content li {
            margin-bottom: 0.5rem;
          }
        `}
      </style>

      <DynamicBanner title={banner.title || "Privacy Policy"} bgImage={banner.images || []}  bgImageMobile={banner.mobile_images || []}/>
      <section className="policysection">
        <div className="container">
          <div className="row mb-5">
            <div className="col-md-12 col-12">
              <div className="res-content">
                
                {/* Title */}
                <div className="sec-title text-center">
                  <h2>{banner.title || "privacy policy"}</h2>
                </div>

                {/* Intro */}
                {introHeading && <div className="text-center mb-3"><strong>{introHeading}</strong></div>}
                {introTextHtml && <div className="mb-4" dangerouslySetInnerHTML={{ __html: introTextHtml }} />}

                {/* 1. Interpretation */}
                <div className="policy-section">
                  <h2 className="mb-4 mt-4 mt-3">{interpretationHeading}</h2>
                  {content.interpretation_text ? (
                    <div dangerouslySetInnerHTML={{ __html: content.interpretation_text }} />
                  ) : (
                    <p>
                      The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
                    </p>
                  )}
                </div>

                {/* 2. Definitions */}
                <div className="policy-section">
                  <h2 className="mb-4 mt-4 mt-3">{definitionsHeading}</h2>
                  <div className="row">
                    <div className="col-md-6">
                      {leftDefs.length > 0 ? leftDefs.map((d, i) => (
                        <div className="definition-item mb-3" key={`left-${i}`}>
                          <div className="definition-header">
                            <span className="def-number">{d.number}</span>
                            <span className="def-content" dangerouslySetInnerHTML={{ __html: d.html }} />
                          </div>
                        </div>
                      )) : <p>No definitions available.</p>}
                    </div>

                    <div className="col-md-6">
                      {rightDefs.length > 0 ? rightDefs.map((d, i) => (
                        <div className="definition-item mb-3" key={`right-${i}`}>
                          <div className="definition-header">
                            <span className="def-number">{d.number}</span>
                            <span className="def-content" dangerouslySetInnerHTML={{ __html: d.html }} />
                          </div>
                        </div>
                      )) : null}
                    </div>
                  </div>
                </div>

                {/* 3. Collecting and Using Your Personal Data */}
                <div className="policy-section">
                  <h2 className="mb-4 mt-4 mt-3">{dataCollectionHeading}</h2>
                  
                  {dataCollectionSections.length > 0 ? (
                    dataCollectionSections.map((section, index) => (
                      <div key={index} className="mb-4">
                        
                        {section.heading && (
                          <h4 className="mb-3">{section.heading}</h4>
                        )}

                        {section.items && section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="mb-3">
                            {item.type === 'summernote' ? (
                                /* Added className "summernote-content" here.
                                   This triggers the <style> block above to fix the bullets.
                                */
                                <div 
                                  className="summernote-content" 
                                  dangerouslySetInnerHTML={{ __html: item.content }} 
                                />
                            ) : (
                                <div style={{ whiteSpace: "pre-line" }}>
                                  {item.content}
                                </div>
                            )}
                          </div>
                        ))}

                      </div>
                    ))
                  ) : (
                    <p>No data collection details available.</p>
                  )}
                </div>

                {/* Contact block */}
                <div className="policy-section">
                  <div className="sec-title text-center">
                    <h2>{contactHeading}</h2>
                  </div>

                  <div className="row d-flex justify-content-center align-items-center">
                    <div className="col-md-6 col-12">
                      <div className="policy-contact">
                        <div className="contact-icon-wrapper">
                          <i className={`fa ${contactIcon}`} />
                        </div>

                        <p>{contactText || "If you have any questions about this Privacy Policy, feel free to reach out."}</p>

                        {contactUrl && (
                          <ul>
                            <li>
                              <a href={contactUrl} target="_blank" rel="noreferrer">
                                <i className="fa fa-globe me-2 text-white"></i> {contactUrl.replace(/^https?:\/\//, "")}
                              </a>
                            </li>
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Extra List */}
                {Array.isArray(content.list) && content.list.length > 0 && (
                  <div className="policy-section">
                    <h2 className="mb-4 mt-4 mt-3">{content.heading || ""}</h2>
                    <ul>
                      {content.list.map((li, idx) => (
                        <li key={idx} dangerouslySetInnerHTML={{ __html: li }} />
                      ))}
                    </ul>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;